/**
 * The per-section rights from the design review's F5 finding.
 *
 * The server enforces these — a response arrives without the tax or payment
 * fields, and a write that carries them is refused. This is the other half:
 * without it a user who cannot edit bank details still sees the Tax & Payment
 * tab, fills it in, and learns the truth from a 400. A control that cannot
 * work should not be offered.
 *
 * Read rights govern whether a section is shown at all. Edit rights govern
 * whether its inputs are live, because "can look, cannot change" is a real
 * state here — a coordinator keeping a profile current without seeing an SSN
 * is the case the split was written for.
 *
 * ## Why an absent code is not a denial
 *
 * The five codes only reach the client once the backend stops listing them in
 * `HIDDEN_PERMISSIONS` — `group_codes_by_category` skips hidden codes when it
 * builds the payload, so before that change the keys are simply *not there*,
 * whatever the user actually holds. Verified against a backend one commit
 * behind: every one of the five came back missing for a GCC Admin holding all
 * of them, which with a plain truthiness check hid the Tax & Payment tab and
 * the compliance Upload button from everybody.
 *
 * So a missing key means "this server does not speak sections yet" and falls
 * back to the general right, which is exactly what migrations `0116`/`0117`
 * did when they backfilled: everyone holding `subcontractor_view` got both
 * view codes, everyone holding `subcontractor_edit` got all three edit codes.
 * An explicit `false` is a real denial and is honoured. The upshot is that
 * this file does not care which repo deploys first.
 */
import { useAppSelector } from "../../../hooks/use-app-selector.ts"

export const TAX_VIEW_PERMISSION =
  "subcontractors.subcontractors.subcontractor_tax_view" as const
export const TAX_EDIT_PERMISSION =
  "subcontractors.subcontractors.subcontractor_tax_edit" as const
export const PAYMENT_VIEW_PERMISSION =
  "subcontractors.subcontractors.subcontractor_payment_view" as const
export const PAYMENT_EDIT_PERMISSION =
  "subcontractors.subcontractors.subcontractor_payment_edit" as const
export const COMPLIANCE_EDIT_PERMISSION =
  "subcontractors.subcontractors.subcontractor_compliance_edit" as const

const GENERAL_VIEW_PERMISSION =
  "subcontractors.subcontractors.subcontractor_view" as const
const GENERAL_EDIT_PERMISSION =
  "subcontractors.subcontractors.subcontractor_edit" as const

/**
 * The raw value at a permission path, `undefined` when the key is absent.
 *
 * `getPermissionValueAtPath` coerces to a boolean, which collapses "denied"
 * and "this server never sent the key" into the same answer — the distinction
 * this hook is built on.
 */
const rawPermissionAtPath = (
  codes: unknown,
  path: string
): unknown | undefined => {
  const [category, section, key] = path.split(".")
  const record = codes as
    | Record<string, Record<string, Record<string, unknown>>>
    | null
    | undefined
  return record?.[category]?.[section]?.[key]
}

export const useSubcontractorSectionPermissions = () => {
  const codes = useAppSelector((state) => state.auth?.permissions?.codes)

  const resolve = (sectionPath: string, generalPath: string): boolean => {
    const value = rawPermissionAtPath(codes, sectionPath)
    if (value === undefined) {
      return !!rawPermissionAtPath(codes, generalPath)
    }
    return !!value
  }

  const mayViewTax = resolve(TAX_VIEW_PERMISSION, GENERAL_VIEW_PERMISSION)
  const mayEditTax = resolve(TAX_EDIT_PERMISSION, GENERAL_EDIT_PERMISSION)
  const mayViewPayment = resolve(
    PAYMENT_VIEW_PERMISSION,
    GENERAL_VIEW_PERMISSION
  )
  const mayEditPayment = resolve(
    PAYMENT_EDIT_PERMISSION,
    GENERAL_EDIT_PERMISSION
  )
  const mayEditCompliance = resolve(
    COMPLIANCE_EDIT_PERMISSION,
    GENERAL_EDIT_PERMISSION
  )

  return {
    mayViewTax,
    mayEditTax,
    mayViewPayment,
    mayEditPayment,
    mayEditCompliance,
    // The tab holds both sections, so it is worth showing if either is.
    mayViewTaxOrPayment: mayViewTax || mayViewPayment,
    // Creation has nothing to read yet, so there the tab turns on the write
    // rights instead — a creator who cannot write either section would
    // otherwise fill in a tab whose every field the server refuses.
    mayEditTaxOrPayment: mayEditTax || mayEditPayment
  }
}
